import { useQueryClient } from '@tanstack/react-query';
import { outreach, outreachQueries } from '@round/api';
import { toast } from '@round/creatorbase-ui';
import { useUpdateOutreachRequestMutation } from '@round/creatorbase-helpers/api-hooks';

const keys = outreachQueries.outreachRequests.keys;

type UpdateNameContext = { previousRequest: outreach.OutreachRequest | undefined };

/**
 * Renaming is applied to the cache before the request goes out, so the headline reads back what was
 * typed rather than flickering through the old name while the PATCH is in flight.
 */
export function useUpdateOutreachRequestName(requestId: number | null | undefined) {
    const queryClient = useQueryClient();
    const detailKey = typeof requestId === 'number' ? keys.detail(requestId) : keys.details();

    const { update, isLoading } = useUpdateOutreachRequestMutation<UpdateNameContext>(requestId, queryClient, {
        onMutate: async ({ name }) => {
            await queryClient.cancelQueries({ queryKey: detailKey });
            const previousRequest = queryClient.getQueryData<outreach.OutreachRequest>(detailKey);

            queryClient.setQueryData<outreach.OutreachRequest>(detailKey, (previous) =>
                previous ? { ...previous, name } : previous
            );

            return { previousRequest };
        },
        onError: (_error, { name }, context) => {
            const { previousRequest } = context ?? {};

            if (previousRequest) {
                queryClient.setQueryData<outreach.OutreachRequest>(detailKey, (current) => {
                    if (!current) {
                        return previousRequest;
                    }

                    // Only roll back while this edit's optimistic name is still the one on show; a
                    // newer commit owns the value and must not be undone by an older failure.
                    if (current.name !== name) {
                        return current;
                    }

                    return { ...current, name: previousRequest.name };
                });
            }

            toast.error("Couldn't rename request");
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: detailKey });
            queryClient.invalidateQueries({ queryKey: keys.lists() });
            queryClient.invalidateQueries({ queryKey: keys.infiniteLists() });
        },
    });

    return { updateName: update, isLoading };
}
